// SPDX-License-Identifier: AGPL-3.0-or-later

import {showDmActionErrorModal} from '@app/features/app/components/alerts/DmActionErrorModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CopyLinkSection} from '@app/features/app/components/dialogs/shared/CopyLinkSection';
import type {RecipientItem} from '@app/features/app/components/dialogs/shared/RecipientList';
import {RecipientList, useRecipientItems} from '@app/features/app/components/dialogs/shared/RecipientList';
import selectorStyles from '@app/features/app/components/dialogs/shared/SelectorModalStyles.module.css';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import {SEARCH_FRIENDS_DESCRIPTOR, SENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Input} from '@app/features/ui/components/form/FormInput';
import {blockIfAccountLimited} from '@app/features/user/utils/AccountLimitUtils';
import {useCopyLinkHandler} from '@app/lib/copy-link';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

export const SEND_TO_FRIEND_DESCRIPTOR = msg({
	message: 'Send to a friend',
	comment:
		'Title of the dialog that sends the link of a feed post to a friend or a group DM, and the tooltip and accessible label of the paper plane button in the action row of a feed post that opens it.',
});
const SEND_DESCRIPTOR = msg({
	message: 'Send',
	comment: 'Button beside a friend in the dialog that sends the link of a feed post as a message.',
});
const logger = new Logger('FeedShareModal');

// Micup: the post link goes out as one plain message, the way the theme and gift links do.
export const FeedShareModal = observer(({url}: {url: string}) => {
	const {i18n} = useLingui();
	const [sentTo, setSentTo] = useState(new Map<string, boolean>());
	const [sendingTo, setSendingTo] = useState(new Set<string>());
	const [searchQuery, setSearchQuery] = useState('');
	const recipients = useRecipientItems();
	const handleCopy = useCopyLinkHandler(url, true);
	const handleSend = async (item: RecipientItem) => {
		if (blockIfAccountLimited()) return;
		const key = item.type === 'group_dm' ? item.id : item.user.id;
		setSendingTo((prev) => new Set(prev).add(key));
		try {
			const channelId = item.channelId ?? (await PrivateChannelCommands.ensureDMChannel(item.user.id));
			const result = await MessageCommands.send(channelId, {
				content: url,
				nonce: SnowflakeUtils.fromTimestamp(Date.now()),
			});
			if (result) setSentTo((prev) => new Map(prev).set(key, true));
		} catch (error) {
			logger.error('Failed to send post link:', error);
			showDmActionErrorModal(error);
		} finally {
			setSendingTo((prev) => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		}
	};
	return (
		<Modal.Root size="small" centered data-flx="feed.feed-share-modal.modal-root">
			<Modal.Header title={i18n._(SEND_TO_FRIEND_DESCRIPTOR)} data-flx="feed.feed-share-modal.modal-header">
				<div className={selectorStyles.headerSearch} data-flx="feed.feed-share-modal.search">
					<Input
						value={searchQuery}
						onChange={(event) => setSearchQuery(event.target.value)}
						placeholder={i18n._(SEARCH_FRIENDS_DESCRIPTOR)}
						leftIcon={
							<MagnifyingGlassIcon
								size={remFromPx(20)}
								weight="bold"
								className={selectorStyles.searchIcon}
								data-flx="feed.feed-share-modal.magnifying-glass-icon"
							/>
						}
						className={selectorStyles.headerSearchInput}
						data-flx="feed.feed-share-modal.input.set-search-query"
					/>
				</div>
			</Modal.Header>
			<Modal.Content className={selectorStyles.selectorContent} data-flx="feed.feed-share-modal.modal-content">
				<RecipientList
					recipients={recipients}
					sendingTo={sendingTo}
					sentTo={sentTo}
					onSend={handleSend}
					defaultButtonLabel={i18n._(SEND_DESCRIPTOR)}
					sentButtonLabel={i18n._(SENT_DESCRIPTOR)}
					scrollerKey="feed-share-modal-friend-list-scroller"
					searchQuery={searchQuery}
					onSearchQueryChange={setSearchQuery}
					showSearchInput={false}
					data-flx="feed.feed-share-modal.recipient-list"
				/>
			</Modal.Content>
			<Modal.Footer data-flx="feed.feed-share-modal.modal-footer">
				<CopyLinkSection
					label={<Trans>Or copy the link:</Trans>}
					value={url}
					onCopy={handleCopy}
					onInputClick={(event) => event.currentTarget.select()}
					data-flx="feed.feed-share-modal.copy-link-section"
				/>
			</Modal.Footer>
		</Modal.Root>
	);
});
